import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { MapPin, Phone, Mail, Clock, ChevronRight, ChevronLeft, Check, Send, MessageCircle, User, Briefcase, Home as HomeIcon, FileText, Users, HelpCircle } from 'lucide-react';
import { PublicLayout } from '@/components/PublicLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { submitContact } from '@/utils/api';
import { toast } from 'sonner';

const Contact = () => {
  const { t, language } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    category: '',
    message: '',
  });

  const totalSteps = 4;

  const categories = [
    {
      id: 'housing',
      icon: HomeIcon,
      title: language === 'fr' ? 'Recherche de logement' : 'Housing search',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'admin',
      icon: FileText,
      title: language === 'fr' ? 'Démarches administratives' : 'Administrative procedures',
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'job',
      icon: Briefcase,
      title: language === 'fr' ? 'Recherche d\'emploi' : 'Job search',
      color: 'from-green-500 to-green-600'
    },
    {
      id: 'integration',
      icon: Users,
      title: language === 'fr' ? 'Intégration' : 'Integration',
      color: 'from-orange-500 to-orange-600'
    },
    {
      id: 'other',
      icon: HelpCircle,
      title: language === 'fr' ? 'Autre demande' : 'Other request',
      color: 'from-slate-500 to-slate-600'
    }
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.name.length >= 2 && formData.email.includes('@');
      case 2:
        return formData.category !== '';
      case 3:
        return formData.subject.length >= 3 && formData.message.length >= 10;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    } else {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs requis' : 'Please fill all required fields');
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(3)) {
      toast.error(language === 'fr' ? 'Veuillez compléter le formulaire' : 'Please complete the form');
      return;
    }

    setLoading(true);

    try {
      const categoryLabel = categories.find(c => c.id === formData.category)?.title || formData.category;
      await submitContact({
        ...formData,
        subject: `[${categoryLabel}] ${formData.subject}`
      });
      toast.success(t.contact.success);
      setCurrentStep(totalSteps);
    } catch (error) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      category: '',
      message: '',
    });
    setCurrentStep(1);
  };

  const contactMethods = [
    {
      icon: Phone,
      title: language === 'fr' ? 'Téléphone' : 'Phone',
      value: '+33 6 81 65 25 38',
      link: 'tel:+33681652538',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      icon: Mail,
      title: 'Email',
      value: 'contact@aile-france.fr',
      link: 'mailto:contact@aile-france.fr',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      icon: MapPin,
      title: language === 'fr' ? 'Adresse' : 'Address',
      value: '1 place Abbé Pierre, 69009 Lyon',
      link: 'https://maps.google.com/?q=1+place+Abb%C3%A9+Pierre+69009+Lyon',
      color: 'bg-green-50 text-green-600'
    },
    {
      icon: Clock,
      title: language === 'fr' ? 'Horaires' : 'Hours',
      value: language === 'fr' ? 'Lun-Ven : 9h-19h' : 'Mon-Fri: 9am-7pm',
      color: 'bg-orange-50 text-orange-600'
    }
  ];

  const faqs = [
    {
      question: language === 'fr' ? 'Quel est le délai de réponse ?' : 'What is the response time?',
      answer: language === 'fr' 
        ? 'Nous répondons à tous les messages dans un délai de 24 heures ouvrables maximum.'
        : 'We respond to all messages within 24 business hours maximum.'
    },
    {
      question: language === 'fr' ? 'Puis-je venir sans rendez-vous ?' : 'Can I come without an appointment?',
      answer: language === 'fr'
        ? 'Oui, notre bureau est ouvert du lundi au vendredi de 9h à 19h. Cependant, prendre rendez-vous garantit qu\'un membre de l\\équipe sera disponible pour vous.'
        : 'Yes, our office is open Monday to Friday from 9am to 7pm. However, making an appointment guarantees that a team member will be available for you.'
    },
    {
      question: language === 'fr' ? 'En quelle langue puis-je vous contacter ?' : 'In what language can I contact you?',
      answer: language === 'fr'
        ? 'Notre équipe parle français, anglais, arabe, espagnol et chinois. N\'hésitez pas à nous écrire dans votre langue !'
        : 'Our team speaks French, English, Arabic, Spanish and Chinese. Don\'t hesitate to write to us in your language!'
    }
  ];

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-white via-primary-50/30 to-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4 px-4 py-2 bg-primary-100 text-[#127abb] rounded-full text-sm font-semibold">
              💬 {language === 'fr' ? 'Nous sommes là pour vous' : 'We are here for you'}
            </div>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-slate-900 mb-6">
              {t.contact.title}
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {t.contact.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactMethods.map((method, index) => {
              const Icon = method.icon;
              return (
                <a
                  key={index}
                  href={method.link || '#'}
                  target={method.link?.startsWith('http') ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  className="flex flex-col items-center text-center p-6 bg-white border-2 border-slate-100 rounded-2xl hover:border-[#127abb] hover:shadow-xl transition-all group"
                >
                  <div className={`w-16 h-16 ${method.color} rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon size={28} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{method.title}</h3>
                  <p className="text-sm text-slate-600">{method.value}</p>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Multi-Step Form */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            {currentStep < totalSteps ? (
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                {/* Progress Bar */}
                <div className="bg-slate-50 px-8 py-6 border-b border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-slate-600">
                      {language === 'fr' ? 'Étape' : 'Step'} {currentStep} {language === 'fr' ? 'sur' : 'of'} {totalSteps - 1}
                    </span>
                    <span className="text-sm font-medium text-[#127abb]">
                      {Math.round((currentStep / (totalSteps - 1)) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-[#127abb] to-[#0e6292] h-full transition-all duration-500 ease-out"
                      style={{ width: `${(currentStep / (totalSteps - 1)) * 100}%` }}
                    ></div>
                  </div>
                </div>

                {/* Form Content */}
                <form onSubmit={handleSubmit} className="p-8 md:p-12">
                  {/* Step 1: Personal Info */}
                  {currentStep === 1 && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right duration-500">
                      <div className="text-center mb-8">
                        <div className="w-20 h-20 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
                          <User className="text-[#127abb]" size={40} strokeWidth={1.5} />
                        </div>
                        <h2 className="text-3xl font-bold text-slate-900 mb-2">
                          {language === 'fr' ? 'Présentez-vous' : 'Introduce yourself'}
                        </h2>
                        <p className="text-slate-600">
                          {language === 'fr' 
                            ? 'Commencez par nous donner vos coordonnées'
                            : 'Start by giving us your contact details'}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          {t.contact.name} *
                        </label>
                        <Input
                          data-testid="contact-name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder={language === 'fr' ? 'Prénom Nom' : 'First Last'}
                          className="h-14 text-lg border-2 border-slate-200 focus:border-[#127abb] rounded-xl"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          {t.contact.email} *
                        </label>
                        <Input
                          data-testid="contact-email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="exemple@email.com"
                          className="h-14 text-lg border-2 border-slate-200 focus:border-[#127abb] rounded-xl"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          {t.contact.phone}
                        </label>
                        <Input
                          data-testid="contact-phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+33 6 12 34 56 78"
                          className="h-14 text-lg border-2 border-slate-200 focus:border-[#127abb] rounded-xl"
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 2: Category Selection */}
                  {currentStep === 2 && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right duration-500">
                      <div className="text-center mb-8">
                        <div className="w-20 h-20 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
                          <MessageCircle className="text-[#127abb]" size={40} strokeWidth={1.5} />
                        </div>
                        <h2 className="text-3xl font-bold text-slate-900 mb-2">
                          {language === 'fr' ? 'Que recherchez-vous ?' : 'What are you looking for?'}
                        </h2>
                        <p className="text-slate-600">
                          {language === 'fr'
                            ? 'Sélectionnez le service qui correspond à votre besoin'
                            : 'Select the service that matches your need'}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {categories.map((category) => {
                          const Icon = category.icon;
                          return (
                            <button
                              key={category.id}
                              type="button"
                              onClick={() => setFormData({ ...formData, category: category.id })}
                              className={`relative p-6 rounded-2xl border-2 transition-all text-left group ${
                                formData.category === category.id
                                  ? 'border-[#127abb] bg-primary-50 shadow-xl scale-105'
                                  : 'border-slate-200 hover:border-[#127abb]/50 hover:shadow-lg'
                              }`}
                            >
                              {formData.category === category.id && (
                                <div className="absolute top-3 right-3 w-6 h-6 bg-[#127abb] rounded-full flex items-center justify-center">
                                  <Check className="text-white" size={16} strokeWidth={3} />
                                </div>
                              )}
                              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center mb-4`}>
                                <Icon className="text-white" size={24} strokeWidth={1.5} />
                              </div>
                              <h3 className="font-bold text-slate-900">{category.title}</h3>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Step 3: Message */}
                  {currentStep === 3 && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right duration-500">
                      <div className="text-center mb-8">
                        <div className="w-20 h-20 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Send className="text-[#127abb]" size={40} strokeWidth={1.5} />
                        </div>
                        <h2 className="text-3xl font-bold text-slate-900 mb-2">
                          {language === 'fr' ? 'Votre message' : 'Your message'}
                        </h2>
                        <p className="text-slate-600">
                          {language === 'fr'
                            ? 'Expliquez-nous votre situation et vos besoins'
                            : 'Explain your situation and needs'}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          {t.contact.subject} *
                        </label>
                        <Input
                          data-testid="contact-subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          placeholder={language === 'fr' ? 'Objet de votre demande' : 'Subject of your request'}
                          className="h-14 text-lg border-2 border-slate-200 focus:border-[#127abb] rounded-xl"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          {t.contact.message} *
                        </label>
                        <Textarea
                          data-testid="contact-message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={8}
                          placeholder={language === 'fr' 
                            ? 'Bonjour,\n\nJe suis étudiant(e) international(e) et j\'ai besoin d\'aide pour...'
                            : 'Hello,\n\nI am an international student and I need help with...'}
                          className="text-lg border-2 border-slate-200 focus:border-[#127abb] rounded-xl"
                        />
                        <p className="text-sm text-slate-500 mt-2">
                          {formData.message.length} {language === 'fr' ? 'caractères' : 'characters'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-200">
                    <Button
                      type="button"
                      onClick={prevStep}
                      disabled={currentStep === 1}
                      variant="outline"
                      className="rounded-full px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={20} className="mr-2" />
                      {language === 'fr' ? 'Précédent' : 'Previous'}
                    </Button>

                    {currentStep < 3 ? (
                      <Button
                        type="button"
                        onClick={nextStep}
                        className="bg-[#127abb] hover:bg-[#0e6292] rounded-full px-8"
                      >
                        {language === 'fr' ? 'Suivant' : 'Next'}
                        <ChevronRight size={20} className="ml-2" />
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        disabled={loading}
                        data-testid="contact-submit-btn"
                        className="bg-[#127abb] hover:bg-[#0e6292] rounded-full px-8 shadow-lg"
                      >
                        {loading ? (
                          language === 'fr' ? 'Envoi...' : 'Sending...'
                        ) : (
                          <>
                            {language === 'fr' ? 'Envoyer' : 'Send'}
                            <Send size={20} className="ml-2" />
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </form>
              </div>
            ) : (
              /* Success Message */
              <div className="bg-white rounded-3xl shadow-2xl p-12 text-center animate-in fade-in zoom-in duration-500">
                <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="text-green-600" size={48} strokeWidth={2} />
                </div>
                <h2 className="text-4xl font-bold text-slate-900 mb-4">
                  {language === 'fr' ? 'Message envoyé !' : 'Message sent!'}
                </h2>
                <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                  {language === 'fr'
                    ? 'Merci pour votre message. Notre équipe vous répondra dans les 24 heures.'
                    : 'Thank you for your message. Our team will respond within 24 hours.'}
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <Button
                    onClick={resetForm}
                    className="bg-[#127abb] hover:bg-[#0e6292] rounded-full px-8"
                  >
                    {language === 'fr' ? 'Envoyer un autre message' : 'Send another message'}
                  </Button>
                  <Link to="/">
                    <Button variant="outline" className="rounded-full px-8">
                      {language === 'fr' ? 'Retour à l\'accueil' : 'Back to home'}
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              {language === 'fr' ? 'Venez nous rencontrer' : 'Come meet us'}
            </h2>
            <p className="text-xl text-slate-600">
              {language === 'fr' ? 'Notre bureau est au cœur de Lyon' : 'Our office is in the heart of Lyon'}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-3xl overflow-hidden shadow-2xl h-96">
              <img
                src="/images/lyon-city.jpg"
                alt="Lyon"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-6">
              <div className="bg-slate-50 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-2">{language === 'fr' ? 'Adresse' : 'Address'}</h3>
                    <p className="text-slate-700">1 place Abbé Pierre</p>
                    <p className="text-slate-700">69009 Lyon, France</p>
                    <a
                      href="https://maps.google.com/?q=1+place+Abb%C3%A9+Pierre+69009+Lyon"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#127abb] hover:underline text-sm mt-2 inline-block"
                    >
                      {language === 'fr' ? 'Voir sur Google Maps' : 'View on Google Maps'} →
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="text-purple-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-2">{language === 'fr' ? 'Horaires d\'ouverture' : 'Opening hours'}</h3>
                    <p className="text-slate-700">{language === 'fr' ? 'Lundi - Vendredi' : 'Monday - Friday'} : 9h00 - 19h00</p>
                    <p className="text-slate-700">{language === 'fr' ? 'Samedi (sur RDV)' : 'Saturday (by appointment)'} : 10h00 - 16h00</p>
                    <p className="text-slate-700">{language === 'fr' ? 'Dimanche : Fermé' : 'Sunday: Closed'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-primary-50 rounded-2xl p-6 border-2 border-[#127abb]">
                <p className="text-slate-700 font-medium">
                  💡 {language === 'fr'
                    ? 'Conseil : Prenez rendez-vous en ligne pour éviter l\'attente et garantir la disponibilité de notre équipe.'
                    : 'Tip: Make an appointment online to avoid waiting and guarantee our team\'s availability.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              {language === 'fr' ? 'Questions fréquentes' : 'FAQ'}
            </h2>
            <p className="text-xl text-slate-600">
              {language === 'fr' ? 'Tout ce que vous devez savoir' : 'Everything you need to know'}
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-white hover:shadow-lg border-2 border-slate-100 hover:border-[#127abb]/30 rounded-2xl p-6 transition-all"
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <h3 className="text-lg font-semibold text-slate-900 pr-4">{faq.question}</h3>
                  <ChevronRight className="text-[#127abb] group-open:rotate-90 transition-transform flex-shrink-0" size={24} />
                </summary>
                <p className="mt-4 text-slate-600 leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#127abb] to-[#0e6292] text-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {language === 'fr' ? 'Vous préférez nous appeler ?' : 'Prefer to call us?'}
          </h2>
          <p className="text-2xl text-primary-100 mb-8">
            {language === 'fr' ? 'Notre équipe est disponible par téléphone' : 'Our team is available by phone'}
          </p>
          <a href="tel:+33681652538">
            <Button size="lg" className="bg-white text-[#127abb] hover:bg-slate-100 rounded-full px-12 py-7 text-xl shadow-2xl">
              <Phone size={24} className="mr-3" />
              +33 6 81 65 25 38
            </Button>
          </a>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Contact;